import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import Tooltip from '../../../components/shared/Tooltip';

interface TeamMember {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

interface TrendBucket {
  periodStart: string;
  videoCount: number;
  averageScore: number;
  signalAverages: Record<string, number>;
}

interface Props {
  isAdmin: boolean;
  teamMembers: TeamMember[];
}

const SIGNAL_LABELS: Record<string, string> = {
  zoneCoverage: 'Zone Coverage',
  faceOnCamera: 'Face On Camera',
  lighting: 'Lighting',
  stability: 'Stability',
  duration: 'Duration',
  speechPace: 'Speech Pace',
  energy: 'Energy',
  buyerName: 'Buyer Name',
  featureCallout: 'Feature Callout',
  cta: 'CTA',
};

const SignalTrendsTab: React.FC<Props> = ({ isAdmin, teamMembers }) => {
  const [granularity, setGranularity] = useState<'week' | 'month'>('week');
  const [selectedSignal, setSelectedSignal] = useState<string>('averageScore');
  const [userId, setUserId] = useState<string>('');
  const [data, setData] = useState<TrendBucket[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('granularity', granularity);
      if (userId) params.set('userId', userId);
      params.set('weeksBack', granularity === 'week' ? '12' : '12');
      const res = await api.get(`/reports/signal-trends?${params.toString()}`);
      setData(res.data.data ?? []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [granularity, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const values = data.map((d) =>
    selectedSignal === 'averageScore'
      ? d.averageScore
      : d.signalAverages[selectedSignal] ?? 0,
  );
  const maxValue = Math.max(100, ...values);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 block mb-1.5">
            Granularity
          </label>
          <div className="inline-flex bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
            {(['week', 'month'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGranularity(g)}
                className={`h-10 px-4 text-[10px] font-black uppercase tracking-widest transition-all ${
                  granularity === g
                    ? 'bg-primary text-white'
                    : 'text-slate-500 dark:text-slate-400 hover:text-primary'
                }`}
              >
                {g === 'week' ? 'Weekly' : 'Monthly'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 block mb-1.5">
            Metric
          </label>
          <select
            value={selectedSignal}
            onChange={(e) => setSelectedSignal(e.target.value)}
            className="h-10 px-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-bold text-slate-600 dark:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 min-w-[200px]"
          >
            <option value="averageScore">Composite Score</option>
            {Object.entries(SIGNAL_LABELS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </div>

        {isAdmin && teamMembers.length > 0 && (
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 block mb-1.5">
              Team Member
            </label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="h-10 px-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-bold text-slate-600 dark:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 min-w-[200px]"
            >
              <option value="">All Team Members</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Chart card */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl shadow-sm">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/20 flex items-center justify-center text-blue-500">
              <span className="material-symbols-outlined text-[24px]">show_chart</span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">
              {selectedSignal === 'averageScore'
                ? 'Composite Score Trend'
                : `${SIGNAL_LABELS[selectedSignal]} Trend`}
            </h2>
          </div>
          <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
            Last 12 {granularity === 'week' ? 'weeks' : 'months'}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
          </div>
        ) : data.length === 0 ? (
          <div className="py-16 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl flex flex-col items-center">
            <span className="material-symbols-outlined text-slate-200 dark:text-slate-800 text-4xl mb-2">
              trending_up
            </span>
            <p className="text-slate-400 dark:text-slate-600 text-sm font-medium">
              No trend data available yet.
            </p>
          </div>
        ) : (
          <div>
            <div className="flex items-end gap-3 h-60 border-b border-slate-200 dark:border-slate-800 pb-3">
              {data.map((d, i) => {
                const v = values[i];
                const pct = maxValue > 0 ? (v / maxValue) * 100 : 0;
                const color =
                  v >= 75
                    ? 'bg-emerald-500'
                    : v >= 50
                      ? 'bg-amber-400'
                      : 'bg-red-400';
                return (
                  <Tooltip key={d.periodStart} content={`${d.videoCount} videos`} placement="bottom" wrapperClassName="flex-1 h-full">
                    <div className="h-full w-full flex flex-col items-center justify-end gap-1 group">
                      <span className="text-[10px] font-black text-slate-400 group-hover:text-primary transition-colors">
                        {v.toFixed(0)}
                      </span>
                      <div
                        className={`w-full ${color} rounded-t-lg opacity-80 group-hover:opacity-100 transition-all`}
                        style={{ height: `${pct}%`, minHeight: v > 0 ? '4px' : '0' }}
                      />
                    </div>
                  </Tooltip>
                );
              })}
            </div>
            <div className="flex gap-3 mt-3">
              {data.map((d) => (
                <div
                  key={d.periodStart}
                  className="flex-1 text-center text-[9px] font-bold uppercase tracking-widest text-slate-400"
                >
                  {granularity === 'week'
                    ? (() => {
                        const start = new Date(d.periodStart);
                        const end = new Date(start);
                        end.setDate(end.getDate() + 6);
                        const fmt = (dt: Date) =>
                          dt.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
                        return `${fmt(start)} – ${fmt(end)}`;
                      })()
                    : new Date(d.periodStart).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalTrendsTab;
